import * as THREE from 'three';
import mapData from '../../server/game/shared/map.json';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { isTouch, BASE_FOV, S } from './state.js';
import { makeSky } from './sky.js';

// ============================================================
//  Рендерер, сцена, камера, риг головы, bloom, свет, пол.
// ============================================================

const app = document.getElementById('app');
// ⚠️ `antialias: false` — и это НЕ отказ от сглаживания, а отказ платить за то,
// чего не было. Когда кадр идёт через `EffectComposer` (а он идёт всегда, пока
// включён блум — то есть по умолчанию), сцена рисуется не в канвас, а в
// собственный буфер композера, у которого MSAA нет вовсе. Флаг канваса при
// этом продолжает действовать на последний полноэкранный проход `OutputPass`:
// четыре выборки на пиксель и разрешение мультисэмплинга — на прямоугольнике,
// где сглаживать нечего, там уже готовая картинка. То есть сглаживания мы не
// получали, а платили за него каждый кадр.
//
// ⚠️ Обратная сторона: с ВЫКЛЮЧЕННЫМ блумом (клавиша B) рендер идёт прямо
// в канвас, и вот там сглаживание было настоящим — его больше нет. Разменяли
// сознательно: путь по умолчанию важнее, а на 85% масштаба рендера MSAA всё
// равно проигрывал бы самому масштабу.
export const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });

/** Потолок плотности пикселей. Выше него не поднимаемся никогда: на экране
 *  с devicePixelRatio 2 честный рендер — вчетверо больше пикселей, а разницу
 *  в движении не видно. */
export const DPR_CAP = Math.min(devicePixelRatio, isTouch ? 1.5 : 2);

/** Пол масштаба рендера.
 *
 *  ⚠️ 0.8, а не «сколько потребуется». Регулятор — не самоцель: его задача
 *  пережить тяжёлый момент, а не купить кадры любой ценой. На 60% картинка
 *  превращается в кашу (проверено владельцем: «даже 60 кадров при 60% — не
 *  очень»), и такие кадры не нужны. Если машина не держит 60 при 80% — честнее
 *  показать 50 кадров, чем мыло; а разбираться надо со стоимостью пикселя,
 *  как в баг-уроке про FPS (md/bug-lessons.md), а не с этой константой. */
const SCALE_FLOOR = 0.8;

/** Текущий масштаб рендера, SCALE_FLOOR…1 от потолка. Крутится сам — см. ниже. */
export let renderScale = 1;
renderer.setPixelRatio(DPR_CAP);
renderer.setSize(innerWidth, innerHeight);

// ⚠️ ПЕРВАЯ СТРОКА ЛЮБОГО РАЗБОРА ПРО FPS. Печатаем, на чём вообще рисуем.
//
// Без неё оптимизация идёт вслепую: половина «странных» замеров объясняется
// не кодом, а тем, что браузер свалился в ПРОГРАММНЫЙ рендеринг (SwiftShader /
// ANGLE software) — тогда видеокарты нет вовсе, стоимость кадра линейна по
// числу пикселей, и ни тени, ни постобработка на неё почти не влияют. Ровно
// эта картина и наблюдалась у владельца, см. md/bug-lessons.md.
//
// Плотность пикселей тут же: «100% масштаба» при devicePixelRatio 2 — это
// вчетверо больше работы, чем при 1, а на глаз разница между ними невелика.
try {
  const gl = renderer.getContext();
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  const gpu = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
  console.log(`[графика] ${gpu} | окно ${innerWidth}×${innerHeight} | DPR ${devicePixelRatio} `
    + `| потолок плотности ${DPR_CAP}`);
} catch { /* расширение могут не дать — не повод падать */ }
// ⚠️ Стартовое значение берётся из сохранённого выбора (S.shadowsOn), а не
// ставится жёстко: применить его ЗДЕСЬ, до того как world.js создаст материалы
// карты, — значит скомпилировать шейдеры сразу без теней, без перекомпиляции.
renderer.shadowMap.enabled = S.shadowsOn;
// ⚠️ PCF, а не PCFSoft. «Soft» — это несколько дополнительных выборок теневой
// карты НА КАЖДЫЙ фрагмент, и стоимость умножается на площадь экрана. Край
// тени становится резче и чуть «лесенкой» — единственное место во всей
// оптимизации, где потеря видна глазом; пошли на неё сознательно, потому что
// альтернативой был масштаб рендера 60%, а вот ОН мылит уже всю картинку,
// а не край тени.
renderer.shadowMap.type = THREE.PCFShadowMap;
app.appendChild(renderer.domElement);
export const canvas = renderer.domElement;

export const scene = new THREE.Scene();
// ⚠️ Цвет фона теперь виден только там, где по какой-то причине не нарисовался
// купол неба (см. ниже), поэтому он равен цвету горизонта, а не «небу вообще»:
// заметить подмену на глаз нельзя, а чёрного экрана при ошибке не будет.
export const SKY_HORIZON = 0xa9d2ea;                   // он же цвет тумана — так стены тают в небе
export const SKY_ZENITH = 0x4f93cf;                    // синева над головой
scene.background = new THREE.Color(SKY_HORIZON);
scene.fog = new THREE.Fog(SKY_HORIZON, 45, 140);       // светлая дымка вдали

export const camera = new THREE.PerspectiveCamera(BASE_FOV, innerWidth / innerHeight, 0.05, 500);

// Риг «головы»: yaw (влево/вправо) → pitch (вверх/вниз) → камера
export const yawObject = new THREE.Object3D();
export const pitchObject = new THREE.Object3D();
pitchObject.add(camera);
yawObject.add(pitchObject);
scene.add(yawObject);

// ---- Post-processing: bloom-свечение ----
export const composer = new EffectComposer(renderer);   // HDR-буфер (HalfFloat) по умолчанию
composer.setPixelRatio(renderer.getPixelRatio());
composer.addPass(new RenderPass(scene, camera));
// порог 1.0 в линейном HDR: блумит только то, что ярче 1 (эмиссивный неон, вспышки), не стены
export const bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 1.1, 0.6, 1.0);
composer.addPass(bloom);
composer.addPass(new OutputPass());                     // корректный перевод HDR→sRGB после блума
bloom.enabled = S.bloomOn;

/**
 * Блум считается в ЧЕТВЕРТИ разрешения экрана.
 *
 * ⚠️ `UnrealBloomPass` сам по себе уже работает в половине (внутри `setSize`
 * делит на 2 и дальше строит цепочку мипов), поэтому «половина от того, что
 * даёт композер» = четверть экрана, и это ещё вчетверо меньше работы.
 * Свечение от этого становится мягче — для блума не порок, а скорее плюс;
 * зато пять размытий во весь экран перестают стоить как вся сцена.
 *
 * ⚠️ Вызывать ПОСЛЕ каждого `composer.setSize`/`setPixelRatio`: композер
 * рассылает свой размер всем пассам и затирает наш.
 */
function applyBloomRes() {
  const p = renderer.getPixelRatio();
  bloom.setSize(Math.max(1, innerWidth * p / 2), Math.max(1, innerHeight * p / 2));
}
applyBloomRes();

// Радиус арены — из общей карты: от него зависят и пол, и охват теней.
const MAP_R = mapData.arena.radius;

// ---- Свет ----
/** Направление НА солнце. ⚠️ Одно на всю игру: отсюда светит DirectionalLight
 *  (то есть куда падают тени) И здесь же рисуется диск на небе (`sky.js`).
 *  Разъедутся — тени пойдут не от солнца, и заметить это можно будет только
 *  глазами, никакой тест такого не ловит. Крутить положение солнца — здесь. */
export const SUN_DIR = new THREE.Vector3(20, 35, 12).normalize();
const SUN_COLOR = 0xfff4e0;

// ⚠️ СООТНОШЕНИЕ «СОЛНЦЕ / ЗАЛИВКА» — это не про красоту, а про то, видно ли
// противника. Раньше стояло 2.6 против 1.3: заливка с неба светила почти
// вполсилы солнца, поэтому теневые грани были почти такими же яркими, как
// освещённые. Карта от этого читалась плоской аппликацией, запечённое
// затенение в углах (world.js) тонуло начисто, а силуэт игрока не имел
// контраста с фоном — ровно та жалоба, с которой всё началось
// («цвета сливаются, не видно персонажей»).
//
// Было 3.1 против 0.8 — и это оказалось перебором в другую сторону. Жалоба
// владельца: «тёмные текстуры под тенью выглядят невзрачно». Считается это
// точно, а не на глаз: у трёх.js заливка с неба даёт освещённость
// `цвет × intensity / π`, то есть при 0.8 теневая грань получала ~8% от того,
// что даёт солнце, — на тёмном материале (дерево, карниз, скала) это 30–40
// единиц из 255, а с запечённым затенением в углу (world.js) уходило почти
// в чёрный. Контраст был не вчетверо, как думали при подборе, а раз в 12.
//
// Сейчас 3.4 против 1.5: теневая сторона стала примерно вдвое светлее,
// солнечная — чуть светлее прежней, контраст остался ~7-кратным (в sRGB это
// хорошо читаемая разница). ⚠️ Мерить это соотношение надо ПОСЛЕ деления на π,
// иначе кажется, что заливка светит вполсилы солнца, а она светит в 12 раз
// слабее.
//
// ⚠️ Нижний цвет заливки поднят (0x6b6252 → 0x8a8070) отдельно от силы:
// он красит грани, смотрящие ВНИЗ, и изнанки козырьков/навесов были самыми
// тёмными местами карты — там нет ни солнца, ни неба.
//
// Цвет заливки по-прежнему теплее снизу и холоднее сверху — дневной свет так
// и работает, а в шутере это ещё и подсказка «где верх».
scene.add(new THREE.HemisphereLight(0xbcd8f5, 0x8a8070, 1.5));
const sun = new THREE.DirectionalLight(SUN_COLOR, 3.4);         // солнце
sun.position.copy(SUN_DIR).multiplyScalar(42); sun.castShadow = S.shadowsOn;
// ⚠️ Размеры теневой камеры и пола идут ОТ РАЗМЕРА КАРТЫ, а не числами.
// Раньше тут стояло ±45 под арену 100×100; на импортированной карте (140×140)
// тени просто обрывались посреди поля, и выглядело это как «пропали тени»,
// хотя ломалась одна константа. Карта у нас теперь может менять размер —
// пусть свет следует за ней сам.
sun.shadow.mapSize.set(2048, 2048);
Object.assign(sun.shadow.camera, { left: -MAP_R, right: MAP_R, top: MAP_R, bottom: -MAP_R });
scene.add(sun);

// ---- Небо ----
// Купол с градиентом, диском солнца и облаками. Всё, что про его устройство и
// цену кадра, — в sky.js; здесь только связка «диск на небе = источник света».
scene.add(makeSky(SUN_DIR, { horizon: SKY_HORIZON, zenith: SKY_ZENITH, sun: SUN_COLOR }));

// ---- Пол ----
function makeGridTexture() {
  const s = 256, c = document.createElement('canvas'); c.width = c.height = s;
  const g = c.getContext('2d');
  g.fillStyle = '#5a6b7d'; g.fillRect(0, 0, s, s);
  g.strokeStyle = '#7c8ea0'; g.lineWidth = 2; g.strokeRect(0, 0, s, s);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(MAP_R, MAP_R);
  // ⚠️ Анизотропия 4, а не максимум (обычно 16). Пол уходит к горизонту и
  // занимает пол-экрана под скользящим углом — ровно тот случай, где каждая
  // лишняя выборка текстуры умножается на площадь. Разницу между 4 и 16
  // на сетке из двух цветов не видно, а платится она на каждом пикселе.
  t.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return t;
}
// Ламберт по той же причине, что и материал карты (см. world.js): PBR на
// некрашеном полу считает блик, которого там нет.
const floor = new THREE.Mesh(new THREE.PlaneGeometry(MAP_R * 2, MAP_R * 2),
  new THREE.MeshLambertMaterial({ map: makeGridTexture() }));
floor.rotation.x = -Math.PI / 2;
// ⚠️ БАГ-УРОК «около пола просвечивает солнце»: плоскость обязана лежать НИЖЕ
// пола карты, а не вровень с ним. Импортированная de_dust2 кладёт плиту земли
// 124×89.5 м так, что её ВЕРХНЯЯ грань ровно на y=0 — то есть раньше эта
// заглушка была с ней копланарна. Две поверхности на одной глубине = z-fighting:
// какая победит, решает округление в последнем бите, и на стыках со стенами
// (там грань карты раздроблена на метровые куски ради AO, а значит глубина
// интерполируется иначе, чем на слитом квадрате) побеждала заглушка. В тёмном
// коридоре это читалось как светлая холодная кайма у основания стен — «щель,
// через которую бьёт солнце». Никакой щели не было: это серо-синяя сетка без
// запечённого затенения. −0.45 м уводит её ниже низа плиты (−0.4) с запасом,
// который перекрывает разрешение буфера глубины на любой дистанции карты.
// ⚠️ Заглушку НЕ убрали: пол карты местами дырявый, и без неё там видно небо.
floor.position.y = -0.45;
floor.receiveShadow = true; scene.add(floor);

/**
 * Тени: включить/выключить на ходу.
 *
 * ⚠️ Мало снять флаг у рендерера — нужно ещё пометить ВСЕ материалы сцены на
 * пересборку. Наличие теней у three.js зашито в текст шейдера через `#define`,
 * а шейдер компилируется один раз и кэшируется: без `needsUpdate` картинка
 * не изменится вовсе, и выглядит это как «переключатель не работает».
 * Разовый подтормаживание на перекомпиляции — нормальная цена, переключают
 * такое раз в жизни.
 *
 * ⚠️ Тени тут не украшение: по ним читается объём и, что важнее для шутера,
 * они выдают присутствие того, кого ещё не видно из-за угла. Выключать —
 * осознанный размен на кадры, а не «настройка по умолчанию».
 */
export function setShadows(on) {
  renderer.shadowMap.enabled = on;
  sun.castShadow = on;
  scene.traverse((o) => {
    if (!o.material) return;
    for (const m of Array.isArray(o.material) ? o.material : [o.material]) m.needsUpdate = true;
  });
}

// Идёт ли игра (есть захват мыши / начата на тач). Живёт здесь, потому что
// нужен и вводу, и гранатам — а модуль scene.js никто из них не импортирует обратно.
export const active = () => isTouch ? S.started : (document.pointerLockElement === canvas);

// ---- Динамическое разрешение ----
//
// ⚠️ ЗАЧЕМ. Игра браузерная, железо у игроков любое, и стоимость кадра здесь
// упирается не в геометрию, а в ПИКСЕЛИ (разбор — баг-урок «FPS упал с 60 до
// 20» в md/bug-lessons.md). Единственная величина, которой можно расплатиться
// за 60 кадров на слабой машине, — внутреннее разрешение рендера. Поэтому оно
// не константа и не настройка в меню, а саморегулирующаяся величина: игра
// меряет собственный FPS и подбирает масштаб под конкретный компьютер.
//
// ⚠️ Масштаб ПОКАЗЫВАЕТСЯ в HUD рядом с FPS, когда он ниже 100%. Молча резать
// чёткость нельзя: игрок должен видеть, что картинка мягче не «просто так».
//
// Ниже `SCALE_FLOOR` не опускаемся (см. его объяснение выше), выше `DPR_CAP`
// не поднимаемся.
export function setRenderScale(s) {
  s = Math.max(SCALE_FLOOR, Math.min(1, s));
  if (Math.abs(s - renderScale) < 0.02) return;   // мелкие шевеления не стоят пересоздания буферов
  renderScale = s;
  const p = DPR_CAP * s;
  renderer.setPixelRatio(p);
  composer.setPixelRatio(p);                       // блум живёт в своих буферах — его тоже
  applyBloomRes();
}

addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight); composer.setSize(innerWidth, innerHeight);
  applyBloomRes();
  // Окно поменяли — прошлый вывод «столько пикселей машина не тянет» больше
  // не про эту площадь: в маленьком окне тянет всё. Разрешаем пробовать заново.
  resetScaleCeil();
});

/** Потолок масштаба: выше него регулятор не поднимается, потому что там уже
 *  не хватило. Живёт здесь, а сбрасывается при resize (см. выше). */
export let scaleCeil = 1;
export function setScaleCeil(v) { scaleCeil = Math.max(SCALE_FLOOR, Math.min(1, v)); }
export function resetScaleCeil() { scaleCeil = 1; }
